/**
 * Server Tablet Page
 * For servers to take dine-in orders at tables
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Tablet, 
  RefreshCw, 
  LogOut, 
  Sun, 
  Moon,
  ShoppingBag,
  UtensilsCrossed,
  Plus,
  Minus,
  Table2,
  Send,
  Bell,
  Check,
  X,
  Search,
  ChevronLeft
} from 'lucide-react';
import { toast } from 'sonner';
import ServerNotification from '@/components/kds/ServerNotification';
import { api } from '@/utils/apiClient';

export default function ServerTabletPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deviceId = searchParams.get('device') || 'SRV-01';
  
  const [isDark, setIsDark] = useState(false);
  const [session, setSession] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Order state
  const [orderType, setOrderType] = useState('dine-in'); // dine-in or takeaway
  const [selectedTable, setSelectedTable] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // UI state
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCart, setShowCart] = useState(false);
  
  // Notifications
  const [notification, setNotification] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const audioRef = useRef(null);

  // Load session and theme
  useEffect(() => {
    const savedSession = localStorage.getItem('kds-session');
    if (savedSession) {
      setSession(JSON.parse(savedSession));
    }
    
    const savedTheme = localStorage.getItem('kds-dark-mode');
    if (savedTheme === 'true') {
      setIsDark(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleTheme = () => {
    setIsDark(!isDark);
    if (!isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('kds-dark-mode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('kds-dark-mode', 'false');
    }
  };

  // Fetch menu items
  const fetchMenu = useCallback(async () => {
    try {
      const response = await api.get('/menu');
      // Handle both array and object response
      const items = Array.isArray(response.data) ? response.data : (response.data.items || []);
      setMenuItems(items);
      
      // Extract unique categories
      const cats = [...new Set(items.map(i => i.category).filter(Boolean))];
      setCategories(cats);
      if (cats.length > 0 && !activeCategory) {
        setActiveCategory(cats[0]);
      }
    } catch (error) {
      console.error('Error fetching menu:', error);
    }
  }, [activeCategory]);

  // Fetch tables
  const fetchTables = useCallback(async () => {
    try {
      const response = await api.get('/api/tables/active');
      setTables(response.data.tables || []);
    } catch (error) {
      console.error('Error fetching tables:', error);
    }
  }, []);

  // Check for notifications
  const checkNotifications = useCallback(async () => {
    try {
      const response = await api.get('/api/kds/notifications/server?acknowledged=false');
      if (response.data.notifications && response.data.notifications.length > 0) {
        setNotification(response.data.notifications[0]);
        // Play sound
        if (soundEnabled && audioRef.current) {
          audioRef.current.play().catch(e => console.log('Audio play failed:', e));
        }
      }
    } catch (error) {
      console.error('Error checking notifications:', error);
    }
  }, [soundEnabled]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchMenu(), fetchTables()]);
      setLoading(false);
    };
    loadData();
    
    // Poll for notifications
    const interval = setInterval(checkNotifications, 10000);
    return () => clearInterval(interval);
  }, [fetchMenu, fetchTables, checkNotifications]);

  // Heartbeat
  useEffect(() => {
    const heartbeat = async () => {
      try {
        await api.post('/api/devices/heartbeat', { device_id: deviceId });
      } catch (error) {
        console.error('Heartbeat error:', error);
      }
    };
    
    heartbeat();
    const interval = setInterval(heartbeat, 60000);
    return () => clearInterval(interval);
  }, [deviceId]);

  // Acknowledge notification
  const handleAcknowledgeNotification = async (notificationId) => {
    try {
      await api.post(`/api/kds/notifications/${notificationId}/acknowledge`, { staff_id: session?.staff_id });
      setNotification(null);
    } catch (error) {
      console.error('Error acknowledging notification:', error);
    }
  };

  // Cart functions
  const addToCart = (item) => {
    const existingIndex = cartItems.findIndex(c => c.id === item.id);
    if (existingIndex >= 0) {
      const newCart = [...cartItems];
      newCart[existingIndex].quantity += 1;
      setCartItems(newCart);
    } else {
      setCartItems([...cartItems, { ...item, quantity: 1 }]);
    }
    toast.success(`Added ${item.name}`);
  };

  const updateQuantity = (index, delta) => {
    const newCart = [...cartItems];
    newCart[index].quantity += delta;
    if (newCart[index].quantity <= 0) {
      newCart.splice(index, 1);
    }
    setCartItems(newCart);
  };

  const removeFromCart = (index) => {
    const newCart = [...cartItems];
    newCart.splice(index, 1);
    setCartItems(newCart);
  };

  const clearCart = () => {
    setCartItems([]);
    setCustomerName('');
    setSpecialInstructions('');
    setSelectedTable(null);
    setOrderType('dine-in');
  };

  const cartTotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Submit order
  const handleSubmitOrder = async () => {
    if (cartItems.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    
    if (orderType === 'dine-in' && !selectedTable) {
      toast.error('Please select a table');
      return;
    }

    setSubmitting(true);
    try {
      const orderData = {
        items: cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          category: item.category,
          customizations: item.customizations || [],
          kds_status: 'pending'
        })),
        customer_name: customerName || 'Guest',
        order_type: orderType,
        table_number: orderType === 'dine-in' ? selectedTable : null,
        order_source: 'server',
        special_instructions: specialInstructions,
        payment_status: 'pending',
        created_by_staff_id: session?.staff_id,
        created_by_device_id: deviceId,
        total: cartTotal
      };

      const response = await api.post('/api/orders', orderData);
      const data = response.data;
      toast.success(`Order #${data.order?.id?.slice(0, 8).toUpperCase()} submitted!`);
      clearCart();
      setShowCart(false);
    } catch (error) {
      console.error('Error submitting order:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit order');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      if (session?.session_id) {
        await api.post('/api/device-sessions/logout', {
          device_id: deviceId,
          session_id: session.session_id
        });
      }
      
      localStorage.removeItem('kds-session');
      localStorage.removeItem('kds-staff');
      localStorage.removeItem('kds-device');
      navigate('/kds/login?device=' + deviceId);
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/kds/login?device=' + deviceId);
    }
  };

  // Filter menu items
  const filteredItems = menuItems.filter(item => {
    const matchesCategory = !activeCategory || item.category === activeCategory;
    const matchesSearch = !searchQuery || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch && item.available !== false;
  });

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'dark bg-gray-900' : 'bg-gray-100'}`}>
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'dark bg-gray-900' : 'bg-gray-100'}`}>
      {/* Hidden audio for notifications */}
      <audio ref={audioRef} src="/sounds/chime.mp3" preload="auto" />
      
      {/* Notification Popup */}
      <ServerNotification
        notification={notification}
        onAcknowledge={handleAcknowledgeNotification}
        show={!!notification}
      />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
              <Tablet className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900 dark:text-white">Server Tablet</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">{deviceId} - {session?.staff_name || 'Guest'}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Notifications Bell */}
            <button
              onClick={checkNotifications}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors relative"
            >
              <Bell className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              {notification && (
                <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              )}
            </button>
            
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-yellow-500" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>
            
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Order Type & Table Selection */}
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 flex items-center gap-4 overflow-x-auto">
          {/* Order Type Toggle */}
          <div className="flex items-center gap-1 bg-white dark:bg-gray-800 rounded-lg p-1 flex-shrink-0">
            <button
              onClick={() => setOrderType('dine-in')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ${
                orderType === 'dine-in' 
                  ? 'bg-blue-500 text-white' 
                  : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              <UtensilsCrossed className="w-4 h-4" />
              Dine-in
            </button>
            <button
              onClick={() => setOrderType('takeaway')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ${
                orderType === 'takeaway' 
                  ? 'bg-orange-500 text-white' 
                  : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              <ShoppingBag className="w-4 h-4" />
              Takeaway
            </button>
          </div>

          {/* Table Selection (for dine-in) */}
          {orderType === 'dine-in' && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <Table2 className="w-4 h-4 text-gray-500" />
              <select
                value={selectedTable || ''}
                onChange={(e) => setSelectedTable(e.target.value ? parseInt(e.target.value) : null)}
                className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="">Select Table</option>
                {tables.map((table) => (
                  <option key={table.table_number} value={table.table_number}>
                    Table {table.table_number}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Cart Button */}
          <button
            onClick={() => setShowCart(true)}
            className="ml-auto px-4 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 flex-shrink-0"
          >
            <ShoppingBag className="w-4 h-4" />
            <span className="font-medium">{cartCount}</span>
            <span className="text-purple-200">${cartTotal.toFixed(2)}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 pb-24">
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search menu..."
            className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-4 -mx-4 px-4">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeCategory === cat
                  ? 'bg-purple-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Menu Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filteredItems.map((item) => (
            <motion.button
              key={item.id}
              whileTap={{ scale: 0.98 }}
              onClick={() => addToCart(item)}
              className="bg-white dark:bg-gray-800 rounded-xl p-3 text-left border border-gray-200 dark:border-gray-700 hover:border-purple-500 transition-colors"
            >
              <h3 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                {item.name}
              </h3>
              <p className="text-purple-600 dark:text-purple-400 font-bold mt-1">
                ${item.price?.toFixed(2)}
              </p>
            </motion.button>
          ))}
        </div>
      </main>

      {/* Cart Drawer */}
      <AnimatePresence>
        {showCart && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setShowCart(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white dark:bg-gray-800 z-50 flex flex-col"
            >
              {/* Cart Header */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowCart(false)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </button>
                  <div>
                    <h2 className="font-bold text-gray-900 dark:text-white">Order</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {orderType === 'dine-in' && selectedTable ? `Table ${selectedTable}` : orderType.toUpperCase()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={clearCart}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Clear
                </button>
              </div>

              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {cartItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <ShoppingBag className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Cart is empty</p>
                  </div>
                ) : (
                  cartItems.map((item, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 flex items-center gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 dark:text-white truncate">
                          {item.name}
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(index, -1)}
                          className="w-8 h-8 rounded-full bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 flex items-center justify-center"
                        >
                          <Minus className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                        </button>
                        <span className="w-8 text-center font-medium text-gray-900 dark:text-white">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(index, 1)}
                          className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}

                {/* Customer Name */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Optional"
                    className="mt-1 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Special Instructions */}
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Special Instructions
                  </label>
                  <textarea
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    placeholder="Any special requests..."
                    rows={2}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  />
                </div>
              </div>

              {/* Cart Footer */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 space-y-3">
                <div className="flex items-center justify-between text-lg font-bold">
                  <span className="text-gray-900 dark:text-white">Total</span>
                  <span className="text-purple-600 dark:text-purple-400">${cartTotal.toFixed(2)}</span>
                </div>
                
                <button
                  onClick={handleSubmitOrder}
                  disabled={cartItems.length === 0 || submitting || (orderType === 'dine-in' && !selectedTable)}
                  className="w-full py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Submit Order
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
